/**
 * Member Activity tab — member credit-ledger history from
 * `GET /v2/admin/members/{contactId}/ledger`. The global `/v2/admin/audit`
 * business-action feed remains separate.
 */
import { useEffect, useRef, useState } from 'react';
import { Avatar, Box, Button, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icon, inkAlpha, MONO, resolveMaps, resolveTokens } from '@/theme';
import {
  getMemberLedger,
  type MemberLedgerEntry,
  type MemberLedgerKind,
  type MemberLedgerPage,
} from '@/api/audit';
import { styleForKind } from '@/features/activity/activityKind';
import { roleLimitedMessage } from '@/components/accessCopy';
import { ledgerReasonLabel } from './ledgerReasons';
import { ScreenError } from '@/components/ScreenError';
import { useCalendarZone } from '@/location/useCalendarZone';
import { DATE_TIME, formatInstant } from '@/utils/instantFormat';

const PAGE_LIMIT = 20;
const SKELETON_ROWS = 5;
const PLACEHOLDER = '—';

type FilterKind = Exclude<MemberLedgerKind, 'UNKNOWN'>;

/** The kinds the endpoint's `kind` filter accepts, in the order the chips show them. */
const FILTER_KINDS: readonly FilterKind[] = ['DEPOSIT', 'DRAW', 'REFUND', 'EXPIRE', 'ADJUST'];

interface FeedState {
  entries: MemberLedgerEntry[];
  /** The next 0-indexed page to read, and whether the endpoint has more rows after it. */
  nextPage: number;
  hasMore: boolean;
  /** The first page of the current filter. */
  status: 'loading' | 'ready' | 'error';
  error: unknown;
  /** A "Load more" read on top of loaded rows. */
  more: 'idle' | 'loading' | 'error';
}

const LOADING: FeedState = {
  entries: [],
  nextPage: 0,
  hasMore: false,
  status: 'loading',
  error: null,
  more: 'idle',
};

/** More rows exist when the count says so; without a count, a full page means "maybe". */
function hasMoreRows(page: MemberLedgerPage, loaded: number): boolean {
  if (page.entries.length === 0) return false;
  return page.totalElements !== null ? loaded < page.totalElements : page.entries.length >= PAGE_LIMIT;
}

/** Append `next`, skipping any transaction already shown (a page never repeats a row). */
function appendUnique(
  prev: readonly MemberLedgerEntry[],
  next: readonly MemberLedgerEntry[],
): MemberLedgerEntry[] {
  const seen = new Set(prev.map((e) => e.transactionId).filter((id): id is string => id !== null));
  const out = [...prev];
  for (const entry of next) {
    if (entry.transactionId !== null) {
      if (seen.has(entry.transactionId)) continue;
      seen.add(entry.transactionId);
    }
    out.push(entry);
  }
  return out;
}

const KIND_LABELS: Record<MemberLedgerKind, string> = {
  DEPOSIT: 'Deposit',
  DRAW: 'Draw',
  REFUND: 'Refund',
  EXPIRE: 'Expiration',
  ADJUST: 'Adjustment',
  UNKNOWN: 'Unknown',
};

function ledgerKindLabel(kind: MemberLedgerKind): string {
  return KIND_LABELS[kind];
}

function ledgerTint(kind: MemberLedgerKind) {
  return styleForKind(kind === 'UNKNOWN' ? null : 'credit');
}

function formatTime(ts: string | null, timeZone: string | null): string {
  // On the venue's clock (DNKADM-0476 F-05), not the browser's.
  return formatInstant(ts, timeZone, DATE_TIME, PLACEHOLDER);
}

function signedAmount(amount: number | null): string | null {
  if (amount === null) return null;
  return `${amount > 0 ? '+' : ''}${amount}`;
}

export function MemberActivityTab({ contactId }: { contactId: string }) {
  const [kind, setKind] = useState<FilterKind | null>(null);
  const [feed, setFeed] = useState<FeedState>(LOADING);
  // Bumped by Try again on a failed first page, to re-run the first-page read.
  const [reloadKey, setReloadKey] = useState(0);
  // Every first-page read starts a new generation; a response from an older one (the
  // filter changed while it was in flight) is dropped, Load more included.
  const generationRef = useRef(0);

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    getMemberLedger(contactId, { page: 0, size: PAGE_LIMIT, ...(kind !== null && { kind }) })
      .then((page) => {
        if (generationRef.current !== generation) return;
        const entries = appendUnique([], page.entries);
        setFeed({
          entries,
          nextPage: 1,
          hasMore: hasMoreRows(page, entries.length),
          status: 'ready',
          error: null,
          more: 'idle',
        });
      })
      .catch((error: unknown) => {
        if (generationRef.current !== generation) return;
        setFeed({ ...LOADING, status: 'error', error });
      });
    return () => {
      // Unmount or a newer read: whatever is in flight must not land.
      if (generationRef.current === generation) generationRef.current = generation + 1;
    };
  }, [contactId, kind, reloadKey]);

  /** Every filter change resets the list, then reads page one. */
  function changeKind(next: FilterKind | null) {
    if (next === kind) return;
    setFeed(LOADING);
    setKind(next);
  }

  function retryFirstPage() {
    setFeed(LOADING);
    setReloadKey((k) => k + 1);
  }

  function loadMore() {
    if (!feed.hasMore || feed.more === 'loading') return;
    const generation = generationRef.current;
    setFeed((prev) => ({ ...prev, more: 'loading' }));
    getMemberLedger(contactId, { page: feed.nextPage, size: PAGE_LIMIT, ...(kind !== null && { kind }) })
      .then((page) => {
        if (generationRef.current !== generation) return;
        setFeed((prev) => {
          const entries = appendUnique(prev.entries, page.entries);
          return {
            ...prev,
            entries,
            nextPage: prev.nextPage + 1,
            hasMore: hasMoreRows(page, entries.length),
            more: 'idle',
          };
        });
      })
      .catch(() => {
        if (generationRef.current !== generation) return;
        // The rows already loaded stay; only the extra page failed.
        setFeed((prev) => ({ ...prev, more: 'error' }));
      });
  }

  const entries = feed.entries;

  if (feed.status === 'error') {
    return (
      <ScreenError
        variant="panel"
        error={feed.error}
        what="the activity log"
        onRetry={retryFirstPage}
        forbiddenMessage={roleLimitedMessage('The activity log', 'OWNER or ADMIN')}
        retryMessage="The activity log could not be loaded. Please try again."
        preferServerMessage
        sx={{ mt: 1 }}
      />
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        role="group"
        aria-label="Filter activity by type"
        sx={{ gap: 1, flexWrap: 'wrap', mb: 1.5 }}
      >
        {[null, ...FILTER_KINDS].map((k) => {
          const selected = kind === k;
          return (
            <Chip
              key={k ?? 'all'}
              label={k === null ? 'All' : KIND_LABELS[k]}
              clickable
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              aria-pressed={selected}
              onClick={() => changeKind(k)}
            />
          );
        })}
      </Stack>

      <Paper elevation={1} sx={{ px: 3, py: 1 }}>
        {feed.status === 'loading' &&
          Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={`sk-${i}`} />)}

        {feed.status === 'ready' && entries.length === 0 && <EmptyState filtered={kind !== null} />}

        {feed.status === 'ready' &&
          entries.map((entry, i) => (
            <ActivityRow
              key={entry.transactionId ?? `row-${i}`}
              entry={entry}
              last={i === entries.length - 1}
            />
          ))}
      </Paper>

      {feed.status === 'ready' && feed.more === 'error' && (
        <Box role="alert" sx={{ mt: 1.5 }}>
          <Typography variant="body2">More activity could not be loaded. Try again.</Typography>
        </Box>
      )}
      {feed.status === 'ready' && feed.hasMore && (
        <Box sx={{ mt: 1.5, textAlign: 'center' }}>
          <Button variant="outlined" onClick={loadMore} disabled={feed.more === 'loading'}>
            {feed.more === 'loading' ? 'Loading…' : feed.more === 'error' ? 'Try again' : 'Load more'}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function ActivityRow({ entry, last }: { entry: MemberLedgerEntry; last: boolean }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const calendarZone = useCalendarZone();
  const tint = ledgerTint(entry.kind);
  const title = entry.reason !== undefined ? ledgerReasonLabel(entry.reason) : ledgerKindLabel(entry.kind);
  const time = formatTime(entry.occurredAt, calendarZone);

  return (
    <Stack
      direction="row"
      sx={{
        gap: 1.75,
        py: 1.375,
        borderBottom: last ? 'none' : `1px solid ${inkAlpha(theme, 0.05)}`,
      }}
    >
      <Avatar
        aria-hidden
        sx={{ width: 34, height: 34, bgcolor: tint.bg, color: tint.fg, fontSize: 18 }}
      >
        <Icon name={tint.icon} />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5 }}>
          <Box component="span" sx={{ fontWeight: 500 }}>
            {title}
          </Box>
          <Box component="span" sx={{ color: t.textMuted }}>
            {' · '}
            {ledgerKindLabel(entry.kind)}
            {' · '}
            {time}
          </Box>
        </Typography>
        <LedgerLine entry={entry} />
        {entry.reason !== undefined && <ReasonPill text={entry.reason} />}
      </Box>
    </Stack>
  );
}

function ReasonPill({ text }: { text: string }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  return (
    <Typography
      sx={{
        fontSize: 12,
        color: t.text2,
        mt: 0.625,
        px: 1.25,
        py: 0.625,
        bgcolor: t.raise,
        borderRadius: 1.5,
        borderLeft: `2px solid ${inkAlpha(theme, 0.15)}`,
      }}
    >
      {ledgerReasonLabel(text)}
    </Typography>
  );
}

function LedgerLine({ entry }: { entry: MemberLedgerEntry }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const maps = resolveMaps(theme);
  const change = signedAmount(entry.amount);
  const changeColor =
    entry.amount !== null && entry.amount !== 0
      ? entry.amount > 0
        ? maps.status.active.fg
        : t.error
      : inkAlpha(theme, 0.7);

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: 1.25,
        mt: 0.375,
        fontSize: 12.5,
        color: inkAlpha(theme, 0.7),
      }}
    >
      {entry.bucketName !== null && <Box component="span">{entry.bucketName}</Box>}
      {change !== null && (
        <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, color: changeColor }}>
          {change}
        </Box>
      )}
      {entry.balanceAfter !== undefined && (
        <Box component="span" sx={{ color: t.textSubtle }}>
          {'· balance '}
          <Box component="span" sx={{ fontFamily: MONO }}>
            {entry.balanceAfter}
          </Box>
        </Box>
      )}
      {entry.lotId !== undefined && (
        <Chip
          size="small"
          label={`Lot ${entry.lotId}`}
          sx={{ height: 20, fontSize: 11, '& .MuiChip-label': { px: 1 } }}
        />
      )}
    </Stack>
  );
}

function SkeletonRow() {
  return (
    <Stack
      direction="row"
      sx={{
        gap: 1.75,
        py: 1.875,
      }}
    >
      <Skeleton variant="circular" width={34} height={34} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width={220} height={13} />
        <Skeleton width={160} height={11} sx={{ mt: 0.5 }} />
      </Box>
    </Stack>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  const theme = useTheme();
  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <Icon name="history" sx={{ fontSize: 42, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 15, fontWeight: 500, mt: 1 }}>
        {filtered ? 'No activity of this type' : 'No ledger activity yet'}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 340, mx: 'auto' }}>
        {filtered
          ? 'Choose All to see every credit movement for this member.'
          : 'Credit grants, draws and refunds for this member will appear here, newest first.'}
      </Typography>
    </Box>
  );
}
